import type { NextRouter } from 'next/router';
import type { SagaIterator } from 'redux-saga';
import { call, getContext, put, select, take, takeEvery } from 'redux-saga/effects';
import type { Client } from '@peloton/api/utils';
import { CLIENT_CONTEXT } from '@peloton/api/utils';
import { isMe } from '@peloton/auth/redux';
import { toOverviewUrl } from '@peloton/links/members';
import {
  getAchievementsForMember,
  loadAchievementsForMember,
} from '@engage/achievements/redux';
import { track } from '@engage/analytics/actions';
import { ME, shouldShowPrivateInfo } from '@engage/members/models';
import { loadMember } from '@engage/members/redux';
import { isMembersSuccessActionForMember } from '@engage/members/sagas';
import { getMember } from '@engage/members/selectors';
import { isMemberPath } from '@members/profile/urls';
import { trackViewedAchievementsPage } from './analytics';
import { toAchievementsUrl } from './urls';

export const loadAchievementsPageSaga = function* (
  client: Client,
  action: any, // Using any type to unblock type erorrs on router.replace calls
): SagaIterator {
  const usernameOrId = action.payload.usernameOrId || ME;
  let member = yield select(getMember, usernameOrId);
  if (!member) {
    yield put(loadMember(usernameOrId));
    const membersAction = yield take(isMembersSuccessActionForMember(usernameOrId));
    member = membersAction.payload;
  }

  const isViewedMemberMe = yield select(isMe, member.id);
  if (isViewedMemberMe && isMemberPath(action.payload.router.asPath)) {
    yield put(
      action.payload.router.replace(toAchievementsUrl(member.id, isViewedMemberMe)),
    );
  }

  const shouldShowPrivateMemberInfo = yield call(shouldShowPrivateInfo, member);
  if (!shouldShowPrivateMemberInfo) {
    yield put(action.payload.router.replace(toOverviewUrl(member.username)));
  } else {
    const achievements = yield select(getAchievementsForMember, member.id);
    if (!achievements) {
      yield put(loadAchievementsForMember(member.id));
    }
  }
  yield put(
    track(
      trackViewedAchievementsPage({
        member,
        sharedLeaderboardTag: action.payload.sharedLeaderboardTag,
      }),
    ),
  );
};

export const achievementsPageSaga = function* (): SagaIterator {
  const client = yield getContext(CLIENT_CONTEXT);
  yield takeEvery(AchievementsPageActionType.Load, loadAchievementsPageSaga, client);
};

enum AchievementsPageActionType {
  Load = 'pelo/page/achievements/Load',
}

export const loadAchievementsPage = (
  sharedLeaderboardTag: string | null,
  router: NextRouter,
  usernameOrId?: string,
) => ({
  type: AchievementsPageActionType.Load,
  payload: { usernameOrId, sharedLeaderboardTag, router },
});
